// @ts-check
import { useSelector } from 'react-redux';

import { GetSearchKeysByView } from 'components/Search/Filters/keys';
import { searchOrigin } from 'components/Search/Results/constants';
import { orgPageExcludedFilters, tokenise } from 'utils/filters';
import { siNumber } from 'utils/number';
import { getFiltersByGroupId } from 'selectors/organisation';
import { useAllCategories } from 'hooks/useDefaultSearchCategories';

import { hasNarrowedCategories, isRestrictedDate } from './carryoverFilters';

// `date` and the "Search across" categories are handled separately below: both are
// populated on every search, so the tokeniser cannot tell an untouched default
// from a deliberate narrowing and would make every profile look filtered.
const excludedFromCount = [...orgPageExcludedFilters, 'date'];

/**
 * Whether anything is narrowing this profile's People and Activity searches.
 *
 * Three things filter these tabs and only one of them produces a token, so all
 * three have to be asked about:
 *   - the filter panel, which the tokeniser covers (and which is what the chips
 *     above the results are drawn from)
 *   - the search bar's term and the Date dropdown, which have no token at all
 *   - the "Search across" categories, which are suppressed as a token because
 *     they are always set, and so are compared against a baseline instead
 *
 * @param {Record<string, any> | null | undefined} filters the org slice's bag
 * @param {Record<string, boolean> | null | undefined} categoryBaseline the selection
 *   "Search across" counts as untouched — see useOrgProfileShowsSubset
 */
export const isOrgProfileFiltered = (filters, categoryBaseline) => {
  const bag = filters ?? {};

  const tokens = tokenise(
    bag,
    GetSearchKeysByView(searchOrigin.organisationPageSearch),
    excludedFromCount
  );
  if (tokens.length > 0) return true;

  if (typeof bag.term === 'string' && bag.term.trim() !== '') return true;
  if (isRestrictedDate(bag.date)) return true;

  return hasNarrowedCategories(bag.workItemCategories, categoryBaseline);
};

/**
 * Whether these figures cover less than the whole organisation.
 *
 * Measured against every category the licence allows rather than against the
 * user's saved default, because a default that leaves categories out hides people
 * just as effectively as a filter typed on the page. They did not choose it here,
 * but the number still has to admit what it is missing.
 *
 * @param {number|string} organisationId
 */
export const useOrgProfileShowsSubset = organisationId => {
  const filters = useSelector(getFiltersByGroupId(organisationId));
  const allCategories = useAllCategories();
  return isOrgProfileFiltered(filters, allCategories);
};

/**
 * Whether a figure has earned an "of <whole organisation>" denominator beside it.
 *
 * The two numbers answer subtly different questions. The numerator is counted
 * live over what this profile's search matched, through the licensed alias; the
 * denominator comes off the organisation document and covers everything indexed
 * against the organisation. So they disagree by a little even when nothing is
 * filtered, which means the gap on its own is not evidence that anything is
 * hidden — only an active filter is.
 *
 * @param {number|undefined} matched what this profile's search returned
 * @param {number|undefined} total the organisation-wide figure
 * @param {boolean} isFiltered whether anything is actually narrowing the profile
 */
export const hasDenominator = (matched, total, isFiltered) =>
  isFiltered &&
  Number.isFinite(matched) &&
  Number.isFinite(total) &&
  // @ts-ignore - guarded by the isFinite checks above
  matched < total;

/**
 * Whether siNumber rounded a figure away — it hands back the number itself up to
 * 999 and a string ("1.5K") beyond.
 * @param {number} value
 */
const isAbbreviated = value => typeof siNumber(value) === 'string';

/**
 * The precise figures behind an abbreviated pair, for a tooltip.
 *
 * Abbreviation is what lets two different numbers render identically, so
 * "16.0K of 16.0K" is truthful but useless on its own — the exact figures have
 * to stay reachable. Returns undefined when nothing was rounded and a tooltip
 * would only repeat what is already on screen.
 *
 * @param {number|undefined} matched
 * @param {number|undefined} [total] omitted when no denominator is shown
 * @returns {string|undefined}
 */
export const exactFigures = (matched, total) => {
  if (!Number.isFinite(matched)) return undefined;

  if (!Number.isFinite(total)) {
    // @ts-ignore - guarded above
    return isAbbreviated(matched) ? matched.toLocaleString() : undefined;
  }

  // @ts-ignore - both guarded above
  if (!isAbbreviated(matched) && !isAbbreviated(total)) return undefined;
  // @ts-ignore - both guarded above
  return `${matched.toLocaleString()} of ${total.toLocaleString()}`;
};
